import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Users,
  MapPin,
  Clock,
  Heart,
  MessageCircle,
  Send,
  Star,
  Trophy,
  Zap,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";

const CATEGORIES = ["all", "general", "progress", "fitness", "fashion", "wellness"];

const BeLife = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [myRsvps, setMyRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [newCategory, setNewCategory] = useState("general");
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState("all");

  // Comments state per post
  const [commentsModal, setCommentsModal] = useState(null); // post or null
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  // Edit / delete state
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("general");
  const [deletingPostId, setDeletingPostId] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchData = async () => {
    try {
      const postsUrl =
        filter === "all" ? "/community/posts" : `/community/posts?category=${filter}`;
      const [eventsRes, postsRes, rsvpsRes] = await Promise.all([
        apiClient.get("/events"),
        apiClient.get(postsUrl),
        apiClient.get("/events/my-rsvps").catch(() => ({ data: [] })),
      ]);
      setEvents(eventsRes.data);
      setPosts(postsRes.data);
      setMyRsvps(rsvpsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const rsvpToEvent = async (eventId) => {
    try {
      const response = await apiClient.post(`/events/${eventId}/rsvp`);
      toast.success(`RSVP confirmed! +${response.data.points_earned} points`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to RSVP");
    }
  };

  const createPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const response = await apiClient.post("/community/posts", {
        content: newPost,
        category: newCategory,
      });
      toast.success(`Post shared! +${response.data.points_earned} points`);
      // Only prepend if it matches current filter
      if (filter === "all" || filter === newCategory) {
        setPosts([response.data, ...posts]);
      }
      setNewPost("");
      setNewCategory("general");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const likePost = async (postId) => {
    try {
      const res = await apiClient.post(`/community/posts/${postId}/like`);
      setPosts(
        posts.map((p) =>
          p.post_id === postId
            ? {
                ...p,
                likes_count: res.data.likes_count,
                liked_by: res.data.liked
                  ? [...(p.liked_by || []), user.user_id]
                  : (p.liked_by || []).filter((id) => id !== user.user_id),
              }
            : p
        )
      );
    } catch (error) {
      console.error("Like error:", error);
    }
  };

  const openComments = async (post) => {
    setCommentsModal(post);
    setComments([]);
    setCommentLoading(true);
    try {
      const res = await apiClient.get(`/community/posts/${post.post_id}/comments`);
      setComments(res.data);
    } catch (error) {
      toast.error("Failed to load comments");
    } finally {
      setCommentLoading(false);
    }
  };

  const submitComment = async () => {
    if (!commentDraft.trim() || !commentsModal) return;
    try {
      const res = await apiClient.post(
        `/community/posts/${commentsModal.post_id}/comments`,
        { content: commentDraft }
      );
      setComments([...comments, res.data]);
      setCommentDraft("");
      setPosts(
        posts.map((p) =>
          p.post_id === commentsModal.post_id
            ? { ...p, comments_count: (p.comments_count || 0) + 1 }
            : p
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await apiClient.delete(`/community/comments/${commentId}`);
      setComments(comments.filter((c) => c.comment_id !== commentId));
      setPosts(
        posts.map((p) =>
          p.post_id === commentsModal.post_id
            ? { ...p, comments_count: Math.max(0, (p.comments_count || 1) - 1) }
            : p
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setEditContent(post.content);
    setEditCategory(post.category || "general");
  };

  const saveEdit = async () => {
    try {
      const res = await apiClient.patch(`/community/posts/${editingPost.post_id}`, {
        content: editContent,
        category: editCategory,
      });
      setPosts(posts.map((p) => (p.post_id === res.data.post_id ? res.data : p)));
      toast.success("Post updated");
      setEditingPost(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update post");
    }
  };

  const deletePost = async () => {
    try {
      await apiClient.delete(`/community/posts/${deletingPostId}`);
      setPosts(posts.filter((p) => p.post_id !== deletingPostId));
      toast.success("Post deleted");
      setDeletingPostId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete post");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString("default", { month: "short" }),
      time: date.toLocaleTimeString("default", { hour: "2-digit", minute: "2-digit" }),
    };
  };

  const isRsvped = (eventId) => myRsvps.some((r) => r.event_id === eventId);
  const isMyPost = (post) => post.user_id === user?.user_id;
  const isLiked = (post) => (post.liked_by || []).includes(user?.user_id);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="be-life-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">BE Life</h1>
          </div>
          <p className="text-muted-foreground">Community events, experiences, and connections.</p>
        </motion.div>

        <Tabs defaultValue="events" className="space-y-8">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="events" data-testid="events-tab">Events</TabsTrigger>
            <TabsTrigger value="community" data-testid="community-tab">Community</TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((event, index) => {
                const date = formatDate(event.date);
                const rsvped = isRsvped(event.event_id);
                return (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full overflow-hidden" data-testid={`event-${event.event_id}`}>
                      <div className="relative aspect-video">
                        <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                        <div className="absolute top-4 left-4 event-date text-white text-center p-2 rounded-xl">
                          <div className="font-heading font-bold text-2xl">{date.day}</div>
                          <div className="text-xs uppercase">{date.month}</div>
                        </div>
                        <Badge className="absolute top-4 right-4 bg-purple-500/90">
                          +{event.points_reward} pts
                        </Badge>
                      </div>
                      <CardContent className="p-6">
                        <Badge variant="outline" className="mb-2 capitalize">{event.category}</Badge>
                        <h3 className="font-heading font-semibold text-lg mb-2">{event.title}</h3>
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{event.description}</p>
                        <div className="space-y-2 mb-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2"><MapPin className="w-4 h-4" />{event.location}</div>
                          <div className="flex items-center gap-2"><Clock className="w-4 h-4" />{date.time}</div>
                          <div className="flex items-center gap-2"><Users className="w-4 h-4" />{event.attendees_count}/{event.capacity} attending</div>
                        </div>
                        <Button
                          className={`w-full ${rsvped ? "bg-purple-500" : ""}`}
                          variant={rsvped ? "default" : "outline"}
                          onClick={() => rsvpToEvent(event.event_id)}
                          disabled={rsvped}
                          data-testid={`rsvp-${event.event_id}`}
                        >
                          {rsvped ? (<><Star className="w-4 h-4 mr-2 fill-current" />RSVPed</>) : "RSVP Now"}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          {/* Community Tab */}
          <TabsContent value="community">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                {/* Create Post */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <Avatar>
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback>{user?.name?.[0] || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-3">
                        <Textarea
                          placeholder="Share your journey... #BEJourney"
                          value={newPost}
                          onChange={(e) => setNewPost(e.target.value)}
                          className="min-h-[100px]"
                          data-testid="new-post-input"
                        />
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Select value={newCategory} onValueChange={setNewCategory}>
                            <SelectTrigger className="w-44" data-testid="new-post-category">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.filter((c) => c !== "all").map((c) => (
                                <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            onClick={createPost}
                            disabled={posting || !newPost.trim()}
                            className="bg-purple-500 hover:bg-purple-600"
                            data-testid="post-btn"
                          >
                            {posting ? "Posting..." : (<><Send className="w-4 h-4 mr-2" />Share</>)}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Filter Chips */}
                <div className="flex flex-wrap gap-2" data-testid="category-filters">
                  {CATEGORIES.map((c) => (
                    <Button
                      key={c}
                      size="sm"
                      variant={filter === c ? "default" : "outline"}
                      onClick={() => setFilter(c)}
                      className={`capitalize ${filter === c ? "bg-purple-500 hover:bg-purple-600" : ""}`}
                      data-testid={`filter-${c}`}
                    >
                      {c}
                    </Button>
                  ))}
                </div>

                {/* Posts Feed */}
                <div className="space-y-4">
                  {posts.length === 0 ? (
                    <Card>
                      <CardContent className="text-center py-12 text-muted-foreground">
                        No posts in this category yet. Be the first to share!
                      </CardContent>
                    </Card>
                  ) : (
                    posts.map((post, index) => (
                      <motion.div
                        key={post.post_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="community-post" data-testid={`post-${post.post_id}`}>
                          <CardContent className="p-6">
                            <div className="flex gap-4">
                              <Avatar>
                                <AvatarImage src={post.user_picture} />
                                <AvatarFallback>{post.user_name?.[0] || "U"}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold">{post.user_name}</span>
                                    <Badge variant="outline" className="text-xs capitalize">
                                      {post.category}
                                    </Badge>
                                    {post.updated_at && (
                                      <span className="text-xs text-muted-foreground">(edited)</span>
                                    )}
                                  </div>
                                  {isMyPost(post) && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          data-testid={`post-menu-${post.post_id}`}
                                        >
                                          <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={() => startEdit(post)}
                                          data-testid={`edit-${post.post_id}`}
                                        >
                                          <Pencil className="w-4 h-4 mr-2" /> Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => setDeletingPostId(post.post_id)}
                                          className="text-destructive"
                                          data-testid={`delete-${post.post_id}`}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                                <p className="text-foreground/90 whitespace-pre-wrap mb-4">
                                  {post.content}
                                </p>
                                {post.image_url && (
                                  <div className="rounded-xl overflow-hidden mb-4">
                                    <img src={post.image_url} alt="" className="w-full h-auto" />
                                  </div>
                                )}
                                <div className="flex items-center gap-4">
                                  <button
                                    onClick={() => likePost(post.post_id)}
                                    className={`flex items-center gap-1 transition-colors ${
                                      isLiked(post) ? "text-red-500" : "text-muted-foreground hover:text-primary"
                                    }`}
                                    data-testid={`like-${post.post_id}`}
                                  >
                                    <Heart className={`w-5 h-5 ${isLiked(post) ? "fill-current" : ""}`} />
                                    <span>{post.likes_count}</span>
                                  </button>
                                  <button
                                    onClick={() => openComments(post)}
                                    className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                                    data-testid={`comments-${post.post_id}`}
                                  >
                                    <MessageCircle className="w-5 h-5" />
                                    <span>{post.comments_count || 0}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-accent" />
                      Top Members
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { name: "Alex K.", points: 4520, badge: "1st" },
                        { name: "Nadia O.", points: 3890, badge: "2nd" },
                        { name: "Marcus A.", points: 3210, badge: "3rd" },
                      ].map((member, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="font-mono">{member.badge}</Badge>
                            <span className="font-medium">{member.name}</span>
                          </div>
                          <span className="text-primary font-bold">{member.points} pts</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Zap className="w-5 h-5 text-blue-500" />
                      Active Challenges
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { name: "Fit February", reward: 500 },
                        { name: "Style Streak", reward: 300 },
                        { name: "Community Builder", reward: 400 },
                      ].map((challenge, i) => (
                        <div key={i} className="p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{challenge.name}</span>
                            <Badge variant="outline">+{challenge.reward} pts</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Comments Dialog */}
      <Dialog open={!!commentsModal} onOpenChange={(o) => !o && setCommentsModal(null)}>
        <DialogContent className="max-w-lg" data-testid="comments-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Comments</DialogTitle>
            <DialogDescription className="line-clamp-2">
              {commentsModal?.content}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[320px] overflow-y-auto space-y-3 pr-1">
            {commentLoading ? (
              <p className="text-sm text-muted-foreground text-center py-6">Loading...</p>
            ) : comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No comments yet. Be the first to reply!
              </p>
            ) : (
              comments.map((c) => (
                <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={c.user_picture} />
                    <AvatarFallback className="text-xs">{c.user_name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{c.user_name}</span>
                      {c.user_id === user?.user_id && (
                        <button
                          onClick={() => deleteComment(c.comment_id)}
                          className="text-xs text-destructive hover:underline"
                          data-testid={`delete-comment-${c.comment_id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap">{c.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(c.created_at).toLocaleString(undefined, {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="Write a comment..."
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitComment()}
              data-testid="comment-input"
            />
            <Button
              onClick={submitComment}
              disabled={!commentDraft.trim()}
              className="bg-purple-500 hover:bg-purple-600"
              data-testid="comment-submit"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog open={!!editingPost} onOpenChange={(o) => !o && setEditingPost(null)}>
        <DialogContent data-testid="edit-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="min-h-[120px]"
              data-testid="edit-content"
            />
            <Select value={editCategory} onValueChange={setEditCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.filter((c) => c !== "all").map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>Cancel</Button>
            <Button
              onClick={saveEdit}
              disabled={!editContent.trim()}
              className="bg-purple-500 hover:bg-purple-600"
              data-testid="save-edit-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPostId} onOpenChange={(o) => !o && setDeletingPostId(null)}>
        <AlertDialogContent data-testid="delete-post-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the post and all its comments. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deletePost}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-post"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default BeLife;
